import { ContentSearchType, SearchCategory } from '@/modules/Search/types';

export const getSearchResultHref = (type: ContentSearchType, id: string, slug?: string | null) => {
  const identifier = slug || id;
  switch (type) {
    case ContentSearchType.Event:
      return `/events/${identifier}`;
    case ContentSearchType.New:
      return `/press-room/${identifier}`;
    case ContentSearchType.Dynamic:
      return `/dynamic/${identifier}`;
    default:
      return `/dynamic/${identifier}`;
  }
};

export const SEARCH_CATEGORIES: SearchCategory[] = [
  { id: ContentSearchType.Event, label: 'Eventos', action: 'NAVIGATE' },
  { id: ContentSearchType.New, label: 'Noticias', action: 'NAVIGATE' },
  { id: ContentSearchType.Procedure, label: 'Trámites', action: 'NAVIGATE' },
  { id: ContentSearchType.Document, label: 'Documentos', action: 'OPEN' },
  { id: ContentSearchType.Regulation, label: 'Normativas', action: 'OPEN' },
  { id: ContentSearchType.Dynamic, label: 'Páginas de navegación', action: 'NAVIGATE' },
];

// Helper function to check if content is Lexical JSON format
const isLexicalFormat = (content: string): boolean => {
  if (!content) return false;
  try {
    const parsed = JSON.parse(content);
    return parsed && parsed.root && parsed.root.children;
  } catch {
    return false;
  }
};

// Helper function to extract text from Lexical JSON
const extractTextFromLexical = (lexicalJson: string): string => {
  try {
    const parsed = JSON.parse(lexicalJson);

    const extractText = (node: any): string => {
      if (!node) return '';

      if (typeof node.text === 'string') {
        return node.text;
      }

      if (Array.isArray(node.children)) {
        return node.children.map(extractText).join(' ');
      }

      return '';
    };

    return extractText(parsed.root);
  } catch {
    return '';
  }
};

export const extractTextFromLexicalOrHtml = (html: string) => {
  if (!html) return '';

  let text = '';
  // Check if it's Lexical format
  if (isLexicalFormat(html)) {
    text = extractTextFromLexical(html);
    return text;
  }

  // Create a temporary div element to parse HTML
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;
  const fullText = tempDiv.textContent || tempDiv.innerText || '';

  return fullText;
};

// Helper function to strip HTML and get plain text, prioritizing search matches
export const stripHtml = (html: string, searchTerm?: string): string => {
  const fullText = extractTextFromLexicalOrHtml(html);

  return processTextSnippet(fullText, searchTerm);
};

// Helper function to process text snippet with search term prioritization
const processTextSnippet = (fullText: string, searchTerm?: string): string => {
  // If no search term, return truncated text from beginning
  if (!searchTerm?.trim()) {
    return fullText.length > 100 ? fullText.substring(0, 100) + '...' : fullText;
  }

  // Find the position of the search term (case insensitive)
  const lowerFullText = fullText.toLowerCase();
  const lowerSearchTerm = searchTerm.toLowerCase();
  const matchIndex = lowerFullText.indexOf(lowerSearchTerm);

  // If no match found, return truncated text from beginning
  if (matchIndex === -1) {
    return fullText.length > 100 ? fullText.substring(0, 100) + '...' : fullText;
  }

  // Calculate start position to center the match in the snippet
  const snippetLength = 100;
  let startPos = Math.max(0, matchIndex - Math.floor((snippetLength - searchTerm.length) / 2));

  // Adjust if we're near the end of the text
  if (startPos + snippetLength > fullText.length) {
    startPos = Math.max(0, fullText.length - snippetLength);
  }

  const snippet = fullText.substring(startPos, startPos + snippetLength);
  const prefix = startPos > 0 ? '...' : '';
  const suffix = startPos + snippetLength < fullText.length ? '...' : '';

  return prefix + snippet + suffix;
};

// Helper function to highlight matching text
export const highlightText = (text: string, searchTerm: string): React.ReactNode => {
  if (!searchTerm.trim()) return text;

  const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
  const parts = text.split(regex);

  return parts.map((part, index) =>
    regex.test(part) ? (
      <mark key={index} className="bg-yellow-200 text-yellow-900 rounded px-0.5">
        {part}
      </mark>
    ) : (
      part
    )
  );
};
